import { useSeoMeta } from '@unhead/react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, Calendar } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { getCategoryById } from '@/lib/categories';
import { useArticles } from '@/hooks/useArticles';
import type { Article } from '@/lib/types';

function formatDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function ArticleListItem({ article, index }: { article: Article; index: number }) {
  return (
    <Link
      to={`/artikel/${article.slug}`}
      className="block animate-fade-in"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <Card className="group overflow-hidden border-border/50 bg-card/50 backdrop-blur hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300">
        <div className="flex flex-col md:flex-row">
          {article.image && (
            <div className="md:w-72 lg:w-80 flex-shrink-0">
              <div className="aspect-video md:aspect-[4/3] h-full overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          )}
          <div className="flex-1 p-6">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(article.publishedAt)}</span>
              {article.quiz && article.quiz.length > 0 && (
                <>
                  <span className="text-border">•</span>
                  <span className="text-primary">{article.quiz.length} Quizfragen</span>
                </>
              )}
            </div>
            <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
              {article.title}
            </h3>
            <p className="text-muted-foreground line-clamp-2 mb-4">{article.summary}</p>
            <div className="flex items-center gap-1 text-sm text-primary font-medium">
              <span>Artikel lesen</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}

function ArticleListSkeleton() {
  return (
    <Card className="overflow-hidden border-border/50 bg-card/50">
      <div className="flex flex-col md:flex-row">
        <Skeleton className="md:w-72 lg:w-80 aspect-video md:aspect-[4/3]" />
        <div className="flex-1 p-6">
          <Skeleton className="h-4 w-32 mb-3" />
          <Skeleton className="h-7 w-3/4 mb-2" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-2/3 mb-4" />
          <Skeleton className="h-4 w-24" />
        </div>
      </div>
    </Card>
  );
}

export function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const category = getCategoryById(categoryId || '');
  const { data: articles, isLoading } = useArticles(category?.tag);

  useSeoMeta({
    title: category ? `${category.name} - wtfisBitcoin` : 'Kategorie nicht gefunden',
    description: category?.description || 'Diese Kategorie existiert nicht.',
  });

  if (!category) {
    return <Navigate to="/" replace />;
  }

  const Icon = category.icon;

  return (
    <Layout>
      {/* Header */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-background" />
        <div className="relative container py-12 lg:py-16">
          <div className="max-w-3xl animate-fade-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold">{category.name}</h1>
              </div>
            </div>
            <p className="text-lg text-muted-foreground">{category.description}</p>
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="container py-8 pb-16">
        {isLoading ? (
          <div className="space-y-6">
            {[...Array(3)].map((_, i) => (
              <ArticleListSkeleton key={i} />
            ))}
          </div>
        ) : articles && articles.length > 0 ? (
          <div className="space-y-6">
            {articles.map((article, index) => (
              <ArticleListItem key={article.id} article={article} index={index} />
            ))}
          </div>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <div className="max-w-md mx-auto space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
                  <Icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold">Noch keine Artikel</h3>
                <p className="text-muted-foreground">
                  In dieser Kategorie gibt es noch keine Artikel. Schau bald wieder vorbei!
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
                >
                  Zurück zur Startseite
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </section>
    </Layout>
  );
}
